if (!customElements.get('age-verifier')) {
  customElements.define(
    'age-verifier',
    class AgeVerifier extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.initialized = false;
        this.showTimer = null;
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.dialog = this.querySelector('[data-age-dialog]');
        this.video = this.querySelector('[data-age-video]');
        this.playButton = this.querySelector('[data-age-play]');
        this.acceptButton = this.querySelector('[data-age-accept]');

        if (!this.dialog || !this.acceptButton) return;

        const signal = this.abortController.signal;
        this.acceptButton.addEventListener('click', () => this.accept(), { signal });
        this.playButton?.addEventListener('click', () => this.playVideo(), { signal });
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.acceptButton.focus();
        }, { signal });
        this.dialog.addEventListener('close', () => this.finalizeClose(), { signal });
        document.addEventListener('shopify:section:unload', (event) => {
          if (event.target?.contains(this)) this.destroy();
        }, { signal });

        if (Shopify.designMode) return;
        if (this.testMode || this.forcePreview || !this.isVerified()) {
          this.showTimer = window.setTimeout(() => this.show(), this.delay * 1000);
        }
      }

      disconnectedCallback() {
        if (!Shopify.designMode) this.destroy();
      }

      get testMode() {
        return this.dataset.testMode === 'true';
      }

      get forcePreview() {
        return new URLSearchParams(window.location.search).has('pp_age_qa');
      }

      get delay() {
        return Number.parseFloat(this.dataset.delay || '1');
      }

      get expiry() {
        return Number.parseInt(this.dataset.expiry || '30', 10);
      }

      isVerified() {
        return this.getCookie('concept:age-verifier') || this.getCookie('pp-age-verified');
      }

      show() {
        if (!this.dialog || this.hasAttribute('open')) return;

        document.querySelectorAll('dialog[open]').forEach((openDialog) => {
          if (openDialog !== this.dialog) openDialog.close();
        });

        if (this.video && !this.video.poster) this.video.poster = this.video.dataset.poster || '';

        this.setAttribute('open', '');
        document.documentElement.classList.add('pp-age-lock');

        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute('open', '');
        }

        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }));
        window.requestAnimationFrame(() => {
          if (this.hasAttribute('open')) this.acceptButton.focus();
        });
      }

      accept() {
        if (!this.testMode) {
          this.setCookie('concept:age-verifier', this.expiry);
          this.setCookie('pp-age-verified', this.expiry);
        }
        this.close();
      }

      close() {
        this.pauseVideo();

        if (this.dialog?.open && typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog?.removeAttribute('open');
          this.finalizeClose();
        }
      }

      finalizeClose() {
        if (!this.hasAttribute('open')) return;
        this.removeAttribute('open');
        this.removeAttribute('playing');
        document.documentElement.classList.remove('pp-age-lock');
        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }));
      }

      async playVideo() {
        if (!this.video) return;

        if (!this.video.src) {
          this.video.src = this.video.dataset.src || '';
          this.video.load();
        }

        try {
          this.video.controls = true;
          await this.video.play();
          this.setAttribute('playing', '');
        } catch (error) {
          this.removeAttribute('playing');
        }
      }

      pauseVideo() {
        if (!this.video) return;
        this.video.pause();
        this.video.currentTime = 0;
      }

      getCookie(name) {
        const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escapedName}=([^;]*)`));
        return match ? match[1] : null;
      }

      setCookie(name, expiry) {
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        document.cookie = `${name}=true; max-age=${expiry * 86400}; path=/; SameSite=Lax${secure}`;
      }

      destroy() {
        window.clearTimeout(this.showTimer);
        this.pauseVideo();
        if (this.dialog?.open) this.dialog.close();
        this.removeAttribute('open');
        document.documentElement.classList.remove('pp-age-lock');
        this.abortController.abort();
      }
    }
  );
}
